import { useState } from "react";
import { NavigationArrow, Spinner } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import API from "../lib/api";

/**
 * "I'm En Route" button for suppliers.
 * Prompts for an ETA in minutes, then posts to the supplier-portal en-route endpoint
 * for the given task. Returns the tracker_token so the parent can reveal the share link.
 *
 * Props:
 *   id           - task id (required)
 *   enRouteAt    - ISO string if already marked en route (renders a chip)
 *   onEnRoute    - callback({ en_route_at, eta_minutes, tracker_token })
 *   compact      - smaller variant for inline lists
 */
export default function EnRouteButton({
  id,
  enRouteAt,
  arrivedAt,
  onEnRoute,
  compact = false,
  className = "",
  "data-testid": testId,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [eta, setEta] = useState("30");
  const [localEnRouteAt, setLocalEnRouteAt] = useState(enRouteAt || null);

  // Hide once arrived — no need to go back to en route
  if (arrivedAt) return null;

  if (localEnRouteAt) {
    return (
      <div
        className={`inline-flex items-center gap-1 px-2 py-1 rounded bg-sky-500/10 text-sky-700 dark:text-sky-400 ${compact ? "text-[10px]" : "text-xs"} font-medium ${className}`}
        data-testid={testId || `en-route-chip-${id}`}
        title={`En route since ${new Date(localEnRouteAt).toLocaleString("en-GB")}`}
      >
        <NavigationArrow size={compact ? 10 : 12} weight="fill" />
        En route
      </div>
    );
  }

  const getPosition = () => new Promise((resolve) => {
    if (!("geolocation" in navigator)) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 0 },
    );
  });

  const submit = async () => {
    const etaNum = parseInt(eta, 10);
    if (!Number.isFinite(etaNum) || etaNum <= 0 || etaNum > 600) {
      toast.error("Please enter a valid ETA in minutes (1–600)");
      return;
    }
    setLoading(true);
    try {
      const coords = await getPosition();
      const { data } = await API.post(`/supplier-portal/tasks/${id}/en-route`, {
        eta_minutes: etaNum,
        ...(coords || {}),
      });
      setLocalEnRouteAt(data.en_route_at);
      toast.success(`Client has been notified — ETA ${etaNum} min`);
      setOpen(false);
      onEnRoute?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark en route");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        size={compact ? "sm" : "sm"}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
        className={`bg-sky-600 hover:bg-sky-700 text-white ${className}`}
        data-testid={testId || `en-route-btn-${id}`}
      >
        <NavigationArrow size={compact ? 12 : 14} weight="fill" className="mr-1" />
        {compact ? "En route" : "I'm en route"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="en-route-dialog">
          <DialogHeader>
            <DialogTitle>On your way?</DialogTitle>
            <DialogDescription>
              Share your ETA so the client can track your arrival in real time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="eta-minutes" className="text-xs">Estimated minutes to arrival</Label>
            <Input
              id="eta-minutes"
              type="number"
              min="1"
              max="600"
              value={eta}
              onChange={(e) => setEta(e.target.value)}
              placeholder="e.g. 25"
              data-testid="eta-minutes-input"
              autoFocus
            />
            <div className="flex flex-wrap gap-1.5">
              {[10, 20, 30, 45, 60].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setEta(String(n))}
                  className={`text-xs px-2.5 py-1 rounded-full border transition ${
                    String(n) === eta
                      ? "bg-sky-600 text-white border-sky-600"
                      : "border-border hover:bg-muted"
                  }`}
                  data-testid={`eta-preset-${n}`}
                >{n} min</button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>Cancel</Button>
            <Button onClick={submit} disabled={loading} data-testid="confirm-en-route-btn" className="bg-sky-600 hover:bg-sky-700 text-white">
              {loading ? <Spinner size={14} className="animate-spin mr-1" /> : <NavigationArrow size={14} weight="fill" className="mr-1" />}
              Confirm & Notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
